import { Link } from 'react-router-dom';
import {
  useStore, Panel, Tile, Badge, Empty, Flash, useFlash, plural, useCan,
} from '../components/ui.jsx';
import {
  dunningQueue, memberById, recordPayment, retryPayment, dashboardStats,
} from '../data/store.js';
import { format } from '../lib/money.js';
import { DUNNING_SCHEDULE, DUNNING_GIVE_UP_DAYS, CHANNEL_LABEL } from '../lib/billing.js';

export default function Billing() {
  const state = useStore();
  const can = useCan();
  const mayCollect = can('billing.write');
  const [flash, setFlash] = useFlash();
  const stats = dashboardStats();
  const queue = dunningQueue();
  const paid = state.invoices.filter((i) => i.status === 'paid');
  const collected = paid.reduce((a, i) => a + i.totalCents, 0);
  const rate = state.invoices.length
    ? Math.round((paid.length / state.invoices.length) * 100)
    : 100;

  return (
    <>
      <header className="topbar">
        <h1>Billing</h1>
        <span className="sub">{plural(state.invoices.length, 'invoice', 'invoices')} this period</span>
      </header>

      <div className="page">
        <Flash flash={flash} />

        <div className="tiles">
          <Tile label="MRR" value={format(stats.mrrCents)} foot="Active memberships" />
          <Tile label="Collected" value={format(collected)} foot={`${paid.length} paid invoices`} />
          <Tile label="Past due" value={format(stats.pastDueCents)} foot={`${stats.pastDueCount} invoices`} />
          <Tile label="Collection rate" value={`${rate}%`} foot="Paid vs. issued" />
        </div>

        {!mayCollect && (
          <div className="notice info">
            You can see what is outstanding, but only an owner or admin can
            retry a payment or mark an invoice paid.
          </div>
        )}

        <Panel title="Recovery queue" bodyless>
          {queue.length === 0 ? <Empty>Nothing outstanding.</Empty> : (
            <table className="data">
              <thead>
                <tr>
                  <th>Member</th><th>Amount</th><th>Failed</th><th>Attempts</th>
                  <th>Next step</th>{mayCollect && <th className="right">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {queue.map(({ invoice, member, step }) => (
                  <tr key={invoice.id}>
                    <td>
                      {member
                        ? <Link to={`/members/${member.id}`}>{member.name}</Link>
                        : <span className="mono">{invoice.memberId}</span>}
                    </td>
                    <td className="mono num">{format(invoice.totalCents, invoice.currency)}</td>
                    <td className="mono num" style={{ color: 'var(--ink-3)' }}>{invoice.failedOnKey}</td>
                    <td className="mono num">{invoice.attemptsMade || 0} of {DUNNING_SCHEDULE.length}</td>
                    <td>
                      {step.action === 'retry' && <Badge tone="bad">{`Retry now · ${CHANNEL_LABEL[step.channel] ?? step.channel}`}</Badge>}
                      {step.action === 'wait' && <Badge tone="warn">{step.nextAttemptOn}</Badge>}
                      {step.action === 'give_up' && <Badge tone="neutral">{`Write off · ${step.ageDays}d`}</Badge>}
                      {step.action === 'needs_review' && <Badge tone="warn">Needs review</Badge>}
                    </td>
                    {mayCollect && (
                    <td className="right">
                      <div className="row" style={{ justifyContent: 'flex-end' }}>
                        <button
                          className="small"
                          disabled={step.action !== 'retry'}
                          onClick={() => { retryPayment(invoice.id); setFlash({ tone: 'good', message: `Retry queued via ${CHANNEL_LABEL[step.channel] ?? step.channel}.` }); }}
                        >
                          Retry
                        </button>
                        <button
                          className="small primary"
                          onClick={() => { recordPayment(invoice.id); setFlash({ tone: 'good', message: 'Payment recorded.' }); }}
                        >
                          Mark paid
                        </button>
                      </div>
                    </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>

        <div className="split">
          <Panel title="All invoices" bodyless>
            <table className="data">
              <thead><tr><th>Member</th><th>Issued</th><th>Status</th><th className="right">Amount</th></tr></thead>
              <tbody>
                {state.invoices.map((i) => (
                  <tr key={i.id}>
                    <td>{memberById(i.memberId)?.name ?? i.memberId}</td>
                    <td className="mono num" style={{ color: 'var(--ink-3)' }}>{i.issuedOn}</td>
                    <td><Badge status={i.status} /></td>
                    <td className="right mono num">{format(i.totalCents, i.currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Panel>

          <Panel title="Dunning schedule">
            <p style={{ marginTop: 0, color: 'var(--ink-2)', fontSize: 13 }}>
              Two fast retries catch temporary declines and payday timing, then it slows
              before handing off. Anything still open after {DUNNING_GIVE_UP_DAYS} days is written off.
            </p>
            <table className="data">
              <thead><tr><th>#</th><th>After</th><th className="right">Channel</th></tr></thead>
              <tbody>
                {DUNNING_SCHEDULE.map((s) => (
                  <tr key={s.attempt}>
                    <td className="mono num">{s.attempt}</td>
                    <td className="mono num">day {s.afterDays}</td>
                    <td className="right"><Badge tone="neutral">{CHANNEL_LABEL[s.channel] ?? s.channel}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Panel>
        </div>
      </div>
    </>
  );
}
